import type { ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import type { LucideIcon } from 'lucide-react';
import { SOURCE_MESSAGES } from '@his/shared';
import { HelpButton } from '@/components/help/HelpButton';
import { useTranslation } from '@/lib/i18n';
import { CLIENT_NAVIGATION, NAVIGATION, PLATFORM_NAVIGATION, longestPrefixMatch, type NavItem, type NavSection } from '@/lib/navigation';
import { navSlug } from '@/lib/navigation-i18n';
import { cn } from '@/lib/utils';

export interface PageIdentity {
  icon: LucideIcon;
  eyebrow: string[];
}

export function pageIdentityFor(pathname: string, lookup: (key: string) => string | null = () => null): PageIdentity | null {
  for (const section of NAVIGATION) {
    const item = longestPrefixMatch(section.items, pathname);
    if (!item) continue;
    return { icon: item.icon, eyebrow: eyebrowFor(section, item, lookup) };
  }
  const flat = longestPrefixMatch<NavItem>([...PLATFORM_NAVIGATION, ...CLIENT_NAVIGATION], pathname);
  return flat ? { icon: flat.icon, eyebrow: [] } : null;
}

function eyebrowFor(section: NavSection, item: NavItem, lookup: (key: string) => string | null): string[] {
  const title = lookup(`nav.section.${navSlug(section.title)}`) ?? section.title;
  const group = item.group ? lookup(`nav.group.${navSlug(item.group)}`) ?? item.group : null;
  return group ? [title, group] : [title];
}

export function PageHeader({
  title,
  description,
  actions,
  help = 'auto',
  icon,
  eyebrow,
  className,
}: {
  title: string;
  description?: string;
  actions?: ReactNode;
  help?: string | 'auto' | false;
  icon?: LucideIcon;
  eyebrow?: string;
  className?: string;
}) {
  const { pathname } = useLocation();
  const shell = useTranslation('shell');
  const lookup = (key: string) => (shell.translated && key in SOURCE_MESSAGES.shell ? shell.t(key) : null);
  const identity = pageIdentityFor(pathname, lookup);
  const Icon = icon ?? identity?.icon;
  const crumbs = eyebrow ? [eyebrow] : identity?.eyebrow ?? [];
  const helpButton = help === false ? null : <HelpButton topicKey={help === 'auto' ? undefined : help} />;

  return (
    <div className={cn('mb-5 flex flex-wrap items-start justify-between gap-3', className)}>
      <div className="flex min-w-0 items-start gap-3">
        {Icon && (
          <span className="mt-0.5 grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-primary/10 text-primary ring-1 ring-inset ring-primary/15">
            <Icon className="h-5 w-5" aria-hidden />
          </span>
        )}
        <div className="min-w-0">
          {crumbs.length > 0 && (
            <p className="mb-0.5 flex flex-wrap items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
              {crumbs.map((crumb, index) => (
                <span key={crumb} className="flex items-center gap-1">
                  {index > 0 && <span aria-hidden>/</span>}
                  {crumb}
                </span>
              ))}
            </p>
          )}
          <h1 className="text-2xl font-semibold leading-tight tracking-tight">{title}</h1>
          {description && <p className="mt-1 text-sm text-muted-foreground">{description}</p>}
        </div>
      </div>
      {(actions || helpButton) && (
        <div className="flex flex-wrap items-center gap-2">
          {actions}
          {helpButton}
        </div>
      )}
    </div>
  );
}
